import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import {
  FileText,
  File as FileIcon,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  Copy,
  Check,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Play,
  AlertTriangle,
} from "lucide-react";
import { Dialog, DialogPortal, DialogOverlay } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { sanitizeOriginalName, annotateAttachmentUrl } from "@/lib/attachment-url";

// Re-export so existing call sites that import `annotateAttachmentUrl` from
// this module keep working. New call sites should import directly from
// `@/lib/attachment-url`.
export { annotateAttachmentUrl };

export type AttachmentKind = "image" | "video" | "pdf" | "other";

export function parseAttachment(url: string): {
  kind: AttachmentKind;
  mime: string | null;
  name: string;
  originalName: string | null;
} {
  let mime: string | null = null;
  let originalName: string | null = null;
  let pathOnly = url;
  try {
    const qIdx = url.indexOf("?");
    if (qIdx >= 0) {
      pathOnly = url.substring(0, qIdx);
      const qs = new URLSearchParams(url.substring(qIdx + 1));
      const ct = qs.get("ct");
      if (ct) mime = ct.toLowerCase();
      // Task #1018 — `?fn=<filename>` carries the human-chosen filename
      // captured at upload time so the lightbox Download action (and any
      // future "forward this attachment" flow) can save the file with the
      // name the staff member or member originally used. Falls back to the
      // path-segment + extension behavior when the annotation is absent.
      const fn = qs.get("fn");
      if (fn) {
        const cleaned = sanitizeOriginalName(fn);
        if (cleaned) originalName = cleaned;
      }
    }
  } catch {
    // fall through to extension-based detection
  }

  const lower = pathOnly.toLowerCase();
  if (mime) {
    if (mime.startsWith("image/")) return { kind: "image", mime, name: fileNameFromPath(pathOnly), originalName };
    if (mime.startsWith("video/")) return { kind: "video", mime, name: fileNameFromPath(pathOnly), originalName };
    if (mime === "application/pdf") return { kind: "pdf", mime, name: fileNameFromPath(pathOnly), originalName };
    return { kind: "other", mime, name: fileNameFromPath(pathOnly), originalName };
  }
  if (/\.(jpe?g|png|webp|gif|heic|heif)$/.test(lower)) return { kind: "image", mime: null, name: fileNameFromPath(pathOnly), originalName };
  if (/\.(mp4|mov|m4v|webm|ogv|avi|mkv|3gp)$/.test(lower)) return { kind: "video", mime: null, name: fileNameFromPath(pathOnly), originalName };
  if (lower.endsWith(".pdf")) return { kind: "pdf", mime: null, name: fileNameFromPath(pathOnly), originalName };
  return { kind: "other", mime: null, name: fileNameFromPath(pathOnly), originalName };
}

function fileNameFromPath(path: string): string {
  const last = path.substring(path.lastIndexOf("/") + 1);
  return last || "attachment";
}

// Original upload filenames are now persisted via the `?fn=` annotation
// (Task #1018). When that's absent (legacy attachments, or inbound MMS where
// the carrier didn't supply a filename), fall back to the storage object's
// path segment with an extension derived from the URL or `?ct=` annotation
// — same shape we used before so the saved file stays recognizable.
const MIME_TO_EXT: Record<string, string> = {
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
  "image/gif": "gif",
  "image/heic": "heic",
  "image/heif": "heif",
};

export function downloadFileNameFor(url: string): string {
  const { name, mime, originalName } = parseAttachment(url);
  if (originalName) return originalName;
  if (/\.[a-z0-9]{2,5}$/i.test(name)) return name;
  const ext = mime ? MIME_TO_EXT[mime] : null;
  return ext ? `${name}.${ext}` : name;
}

function absoluteUrl(url: string): string {
  try {
    if (typeof window !== "undefined") {
      return new URL(url, window.location.href).toString();
    }
  } catch {
    // Fall through and return the raw url.
  }
  return url;
}

async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy fallback below.
  }
  if (typeof document === "undefined") return false;
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

async function downloadImage(url: string, filename: string): Promise<void> {
  // External URLs (PadSplit-hosted attachments, etc.) can't be fetched with
  // credentials because of CORS. For those, fall back to a simple anchor
  // download — the browser will hand the URL to the OS download manager.
  const isExternal =
    typeof window !== "undefined" &&
    !url.startsWith("/") &&
    !url.startsWith(window.location.origin);

  if (isExternal) {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return;
  }

  let objectUrl: string | null = null;
  try {
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.rel = "noopener";
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    if (objectUrl) {
      // Defer revoke so the click has time to start the download in all browsers.
      setTimeout(() => URL.revokeObjectURL(objectUrl!), 1000);
    }
  }
}

type Tone = "light" | "dark";

export interface MessageAttachmentsProps {
  urls: string[] | null | undefined;
  messageId: number | string;
  tone?: Tone;
}

export function MessageAttachments({ urls, messageId, tone = "light" }: MessageAttachmentsProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [videoLightboxIndex, setVideoLightboxIndex] = useState<number | null>(null);

  const imageUrls = useMemo(() => {
    if (!urls) return [] as string[];
    return urls.filter((u) => parseAttachment(u).kind === "image");
  }, [urls]);

  const videoUrls = useMemo(() => {
    if (!urls) return [] as string[];
    return urls.filter((u) => parseAttachment(u).kind === "video");
  }, [urls]);

  if (!urls || urls.length === 0) return null;
  const isDark = tone === "dark";
  const chipBaseClass = isDark
    ? "bg-white/15 text-white border border-white/30 hover:bg-white/25"
    : "bg-white dark:bg-gray-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 hover:bg-blue-50 dark:hover:bg-blue-900/40";
  const otherChipClass = isDark
    ? "bg-white/10 text-white border border-white/20 hover:bg-white/20"
    : "bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800";

  let runningImageIdx = -1;
  let runningVideoIdx = -1;

  return (
    <>
      <div className="mt-2 flex flex-wrap gap-2" data-testid={`message-attachments-${messageId}`}>
        {urls.map((url, idx) => {
          const key = `${messageId}-${idx}`;
          const { kind, name } = parseAttachment(url);
          if (kind === "image") {
            runningImageIdx += 1;
            const imageIdx = runningImageIdx;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setLightboxIndex(imageIdx)}
                className="block group rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                data-testid={`attachment-image-${messageId}-${idx}`}
                aria-label="Open attached image"
              >
                <img
                  src={url}
                  alt="Attachment"
                  loading="lazy"
                  className="h-28 w-28 rounded-md object-cover border border-gray-200 dark:border-gray-700 transition-opacity group-hover:opacity-90"
                />
              </button>
            );
          }
          if (kind === "video") {
            runningVideoIdx += 1;
            const videoIdx = runningVideoIdx;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setVideoLightboxIndex(videoIdx)}
                className="relative block group rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                data-testid={`attachment-video-${messageId}-${idx}`}
                aria-label="Play attached video"
              >
                <video
                  src={url}
                  preload="metadata"
                  muted
                  playsInline
                  className="h-28 w-28 rounded-md object-cover border border-gray-200 dark:border-gray-700 bg-black transition-opacity group-hover:opacity-90"
                />
                <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white shadow-sm">
                    <Play className="h-4 w-4 translate-x-[1px]" fill="currentColor" />
                  </span>
                </span>
              </button>
            );
          }
          if (kind === "pdf") {
            return (
              <a
                key={key}
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${chipBaseClass}`}
                data-testid={`attachment-pdf-${messageId}-${idx}`}
              >
                <FileText className="h-3.5 w-3.5" />
                <span>View PDF</span>
              </a>
            );
          }
          return (
            <a
              key={key}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${otherChipClass}`}
              data-testid={`attachment-file-${messageId}-${idx}`}
            >
              <FileIcon className="h-3.5 w-3.5" />
              <span className="truncate max-w-[160px]">{name}</span>
            </a>
          );
        })}
      </div>
      <PhotoLightbox
        images={imageUrls}
        index={lightboxIndex}
        messageId={messageId}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
      <VideoLightbox
        videos={videoUrls}
        index={videoLightboxIndex}
        messageId={messageId}
        onIndexChange={setVideoLightboxIndex}
        onClose={() => setVideoLightboxIndex(null)}
      />
    </>
  );
}

export interface AttachmentPreviewsProps {
  urls: string[] | null | undefined;
  /** Stable prefix used for test ids and the lightbox key. */
  idPrefix: string | number;
  /**
   * When provided, each attachment renders a small remove control (used by
   * editable proof lists). The callback receives the index into `urls`. Omit
   * for read-only surfaces (e.g. the Payment History "Proof" column).
   */
  onRemove?: (idx: number) => void;
}

/**
 * Compact inline previews for a list of attachment URLs: image attachments
 * render as small clickable thumbnails that open the shared {@link PhotoLightbox}
 * (with zoom + download), while PDFs and other files keep an icon + filename
 * link. Used in dense surfaces like the Payment History "Proof" column where the
 * larger {@link MessageAttachments} layout would be too heavy. Pass `onRemove`
 * to make the list editable (staged uploads in the manual-payment forms).
 */
export function AttachmentPreviews({ urls, idPrefix, onRemove }: AttachmentPreviewsProps) {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const imageUrls = useMemo(() => {
    if (!urls) return [] as string[];
    return urls.filter((u) => parseAttachment(u).kind === "image");
  }, [urls]);

  if (!urls || urls.length === 0) return null;

  let runningImageIdx = -1;

  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5" data-testid={`attachment-previews-${idPrefix}`}>
        {urls.map((url, idx) => {
          const att = parseAttachment(url);
          const name = att.originalName || downloadFileNameFor(url);
          if (att.kind === "image") {
            runningImageIdx += 1;
            const imageIdx = runningImageIdx;
            return (
              <div key={idx} className="relative">
                <button
                  type="button"
                  onClick={() => setLightboxIndex(imageIdx)}
                  className="block group rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                  data-testid={`attachment-preview-image-${idPrefix}-${idx}`}
                  aria-label="Open proof image"
                  title={name}
                >
                  <img
                    src={url}
                    alt={name}
                    loading="lazy"
                    className="h-10 w-10 rounded-md object-cover border border-gray-200 dark:border-gray-700 transition-opacity group-hover:opacity-90"
                  />
                </button>
                {onRemove && (
                  <button
                    type="button"
                    onClick={() => onRemove(idx)}
                    className="absolute -top-1.5 -right-1.5 rounded-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-0.5 text-gray-400 hover:text-red-600 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                    aria-label="Remove attachment"
                    data-testid={`attachment-preview-remove-${idPrefix}-${idx}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            );
          }
          return (
            <span key={idx} className="inline-flex items-center gap-1">
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-brand hover:underline max-w-[180px]"
                data-testid={`attachment-preview-file-${idPrefix}-${idx}`}
                title={name}
              >
                {att.kind === "pdf"
                  ? <FileText className="h-3.5 w-3.5 shrink-0" />
                  : <FileIcon className="h-3.5 w-3.5 shrink-0" />}
                <span className="truncate">{name}</span>
              </a>
              {onRemove && (
                <button
                  type="button"
                  onClick={() => onRemove(idx)}
                  className="shrink-0 text-gray-400 hover:text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                  aria-label="Remove attachment"
                  data-testid={`attachment-preview-remove-${idPrefix}-${idx}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      <PhotoLightbox
        images={imageUrls}
        index={lightboxIndex}
        messageId={idPrefix}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
      />
    </>
  );
}

interface VideoLightboxProps {
  videos: string[];
  index: number | null;
  messageId: number | string;
  onIndexChange: (idx: number) => void;
  onClose: () => void;
}

function VideoLightbox({ videos, index, messageId, onIndexChange, onClose }: VideoLightboxProps) {
  const open = index !== null && index >= 0 && index < videos.length;
  const total = videos.length;
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  const goPrev = useCallback(() => {
    if (index === null || total === 0) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (index === null || total === 0) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  // Reset error state whenever the displayed video (or open state) changes so
  // navigating between videos clears any previous failure.
  useEffect(() => {
    setLoadError(false);
  }, [index, open]);

  const handleRetry = useCallback(() => {
    setLoadError(false);
    const v = videoRef.current;
    if (v) {
      try {
        v.load();
      } catch {
        // Ignore — the onError handler will re-fire if loading fails again.
      }
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      // Don't hijack arrow keys while the user is scrubbing the native video
      // controls — the <video> element handles seek itself when focused.
      const isVideo = tag === "VIDEO";
      if (e.key === "ArrowLeft" && !isVideo) {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight" && !isVideo) {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, goPrev, goNext]);

  const currentUrl = open ? videos[index!] : null;
  const showNav = total > 1;

  const handleDownload = useCallback(async () => {
    if (!currentUrl || downloading) return;
    setDownloading(true);
    try {
      await downloadImage(currentUrl, downloadFileNameFor(currentUrl));
    } catch {
      toast({
        title: "Download failed",
        description: "The video could not be downloaded. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  }, [currentUrl, downloading, toast]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-50 focus:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
          data-testid={`video-lightbox-${messageId}`}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">Video attachment player</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {showNav ? `Video ${(index ?? 0) + 1} of ${total}.` : "Attached video"}
          </DialogPrimitive.Description>

          <button
            type="button"
            className="absolute inset-0 w-full h-full cursor-default focus:outline-none"
            onClick={onClose}
            aria-label="Close video player"
            data-testid={`video-lightbox-backdrop-${messageId}`}
          />

          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 sm:p-8">
            {currentUrl && !loadError && (
              <video
                key={currentUrl}
                ref={videoRef}
                src={currentUrl}
                controls
                autoPlay
                playsInline
                className="pointer-events-auto max-h-full max-w-full rounded-md bg-black shadow-2xl"
                data-testid={`video-lightbox-player-${messageId}`}
                onClick={(e) => e.stopPropagation()}
                onError={() => setLoadError(true)}
              />
            )}
            {currentUrl && loadError && (
              <div
                className="pointer-events-auto flex max-w-md flex-col items-center gap-3 rounded-md bg-gray-900 px-6 py-6 text-center text-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid={`video-lightbox-error-${messageId}`}
              >
                <AlertTriangle className="h-8 w-8 text-amber-400" aria-hidden="true" />
                <div className="text-sm font-medium">Couldn't load this video</div>
                <div className="text-xs text-white/70">
                  Check your connection and try again, or download the file to view it locally.
                </div>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    data-testid={`video-lightbox-retry-${messageId}`}
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid={`video-lightbox-error-download-${messageId}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </button>
                </div>
              </div>
            )}
          </div>

          <div
            className="absolute top-4 right-4 z-10 flex items-center gap-2"
            data-testid={`video-lightbox-toolbar-${messageId}`}
          >
            <button
              type="button"
              onClick={handleDownload}
              disabled={!currentUrl || downloading}
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Download video"
              title="Download video"
              data-testid={`video-lightbox-download-${messageId}`}
            >
              <Download className="h-5 w-5" />
            </button>
            <DialogPrimitive.Close
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Close video player"
              data-testid={`video-lightbox-close-${messageId}`}
            >
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>

          {showNav && (
            <div
              className="absolute top-4 left-4 z-10 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white"
              data-testid={`video-lightbox-counter-${messageId}`}
            >
              {(index ?? 0) + 1} / {total}
            </div>
          )}

          {showNav && (
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-2 sm:left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Previous video"
              data-testid={`video-lightbox-prev-${messageId}`}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {showNav && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 sm:right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Next video"
              data-testid={`video-lightbox-next-${messageId}`}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

interface PhotoLightboxProps {
  images: string[];
  index: number | null;
  messageId: number | string;
  onIndexChange: (idx: number) => void;
  onClose: () => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const ZOOM_STEP = 1.5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_DIST_PX = 30;
const DOUBLE_TAP_SCALE = 2;

function clampScale(v: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, v));
}

export function PhotoLightbox({ images, index, messageId, onIndexChange, onClose }: PhotoLightboxProps) {
  const open = index !== null && index >= 0 && index < images.length;
  const total = images.length;
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  // Bumping this nonce changes the <img>'s key, forcing the browser to
  // discard the cached failure and re-issue the request when the user clicks
  // "Try again" after a previous load failed.
  const [retryNonce, setRetryNonce] = useState(0);

  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const imageRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{ startX: number; startY: number; offsetX: number; offsetY: number } | null>(null);
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchRef = useRef<{
    startDistance: number;
    startScale: number;
    startMidX: number;
    startMidY: number;
    startOffsetX: number;
    startOffsetY: number;
    centerX: number;
    centerY: number;
  } | null>(null);
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null);

  // Mirror scale/offset into refs so handlers bound via addEventListener can
  // read the latest values without re-binding on every state change.
  const scaleRef = useRef(scale);
  const offsetRef = useRef(offset);
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);
  useEffect(() => {
    offsetRef.current = offset;
  }, [offset]);

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Reset zoom/pan whenever the displayed image (or open state) changes.
  useEffect(() => {
    reset();
    lastTapRef.current = null;
  }, [index, reset]);

  // Clear the failure UI whenever the displayed image (or open state)
  // changes so navigating between images or reopening the lightbox starts
  // fresh.
  useEffect(() => {
    setLoadError(false);
    setRetryNonce(0);
  }, [index, open]);

  const handleRetry = useCallback(() => {
    setLoadError(false);
    setRetryNonce((n) => n + 1);
  }, []);

  // Clamp a candidate pan offset so the scaled image always covers the
  // visible viewport (or stays centered on an axis where the scaled image is
  // smaller than the viewport). Uses the image's layout box and its parent
  // container's content box as the source of truth so the math stays in sync
  // with whatever the browser actually renders (object-contain, padding, etc).
  const clampOffset = useCallback(
    (next: { x: number; y: number }, s: number) => {
      const img = imageRef.current;
      const parent = img?.parentElement;
      if (!img || !parent) return next;
      const boxW = parent.clientWidth;
      const boxH = parent.clientHeight;
      // clientWidth/Height on an <img> reflect the laid-out box (after
      // max-w/max-h constraints) at scale 1, which matches the displayed
      // image size with object-contain since the box already has the image's
      // aspect ratio.
      const dispW = img.clientWidth;
      const dispH = img.clientHeight;
      const scaledW = dispW * s;
      const scaledH = dispH * s;
      const maxX = Math.max(0, (scaledW - boxW) / 2);
      const maxY = Math.max(0, (scaledH - boxH) / 2);
      return {
        x: scaledW <= boxW ? 0 : Math.max(-maxX, Math.min(maxX, next.x)),
        y: scaledH <= boxH ? 0 : Math.max(-maxY, Math.min(maxY, next.y)),
      };
    },
    [],
  );

  const goPrev = useCallback(() => {
    if (index === null || total === 0) return;
    onIndexChange((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (index === null || total === 0) return;
    onIndexChange((index + 1) % total);
  }, [index, total, onIndexChange]);

  const zoomIn = useCallback(() => {
    setScale((s) => {
      const next = clampScale(+(s * ZOOM_STEP).toFixed(3));
      setOffset((prev) => clampOffset(prev, next));
      return next;
    });
  }, [clampOffset]);

  const zoomOut = useCallback(() => {
    setScale((s) => {
      const next = clampScale(+(s / ZOOM_STEP).toFixed(3));
      if (next <= MIN_SCALE) {
        setOffset({ x: 0, y: 0 });
        return MIN_SCALE;
      }
      setOffset((prev) => clampOffset(prev, next));
      return next;
    });
  }, [clampOffset]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "+" || e.key === "=") {
        e.preventDefault();
        zoomIn();
      } else if (e.key === "-" || e.key === "_") {
        e.preventDefault();
        zoomOut();
      } else if (e.key === "0") {
        e.preventDefault();
        reset();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, goPrev, goNext, zoomIn, zoomOut, reset]);

  // Wheel zoom — bind via addEventListener so we can use { passive: false } and
  // call preventDefault to keep the page from scrolling behind the dialog.
  // Zooms are anchored at the cursor so the pixel under the pointer stays put
  // as the image grows or shrinks.
  useEffect(() => {
    if (!open) return;
    const el = imageRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
      const currentScale = scaleRef.current;
      const next = clampScale(+(currentScale * factor).toFixed(3));
      if (next === currentScale) return;
      if (next <= MIN_SCALE) {
        setScale(MIN_SCALE);
        setOffset({ x: 0, y: 0 });
        return;
      }
      // The image has transformOrigin: center center, so its visual center on
      // screen equals its layout center plus the current offset. Reading the
      // bounding rect gives us that visual center directly. To keep the pixel
      // under the cursor stationary across a scale change from s -> s', we
      // adjust the offset by (cursor - visualCenter) * (1 - s'/s). The result
      // is then clamped so the image still covers the viewport.
      const rect = el.getBoundingClientRect();
      const cx = (rect.left + rect.right) / 2;
      const cy = (rect.top + rect.bottom) / 2;
      const ratio = next / currentScale;
      setScale(next);
      setOffset(
        clampOffset(
          {
            x: offsetRef.current.x + (e.clientX - cx) * (1 - ratio),
            y: offsetRef.current.y + (e.clientY - cy) * (1 - ratio),
          },
          next,
        ),
      );
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [open, index, clampOffset]);

  // Re-clamp the offset when the viewport resizes so resizing the window
  // can't leave a previously-valid offset pointing off-screen.
  useEffect(() => {
    if (!open) return;
    const handler = () => {
      setOffset((prev) => clampOffset(prev, scale));
    };
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [open, scale, clampOffset]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      try {
        (e.target as HTMLElement).setPointerCapture(e.pointerId);
      } catch {
        // ignore — some browsers reject capture on certain element types
      }
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointersRef.current.size === 2) {
        // A second finger down means this is a pinch — cancel any pending
        // double-tap so a fingers-down/up/down-with-pinch sequence does not
        // accidentally trigger a zoom toggle.
        lastTapRef.current = null;
        const pts = Array.from(pointersRef.current.values());
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;
        // Capture the visual image center at pinch start so we can later
        // anchor the zoom at the starting midpoint between the two fingers.
        const el = imageRef.current;
        let cx = midX;
        let cy = midY;
        if (el) {
          const rect = el.getBoundingClientRect();
          cx = (rect.left + rect.right) / 2;
          cy = (rect.top + rect.bottom) / 2;
        }
        pinchRef.current = {
          startDistance: Math.hypot(dx, dy) || 1,
          startScale: scale,
          startMidX: midX,
          startMidY: midY,
          startOffsetX: offset.x,
          startOffsetY: offset.y,
          centerX: cx,
          centerY: cy,
        };
        dragRef.current = null;
      } else if (pointersRef.current.size === 1) {
        // Detect a double-tap: a second pointerdown within DOUBLE_TAP_MS and
        // close to the first. Mirrors the iOS Photos / Google Photos gesture:
        // double-tap zooms in to 200% centered on the tap, double-tap again
        // resets to fit-to-screen.
        const now = Date.now();
        const last = lastTapRef.current;
        if (
          last &&
          now - last.time < DOUBLE_TAP_MS &&
          Math.hypot(e.clientX - last.x, e.clientY - last.y) < DOUBLE_TAP_DIST_PX
        ) {
          lastTapRef.current = null;
          dragRef.current = null;
          if (scale > 1) {
            reset();
          } else {
            const img = imageRef.current;
            if (img) {
              const rect = img.getBoundingClientRect();
              const centerX = (rect.left + rect.right) / 2;
              const centerY = (rect.top + rect.bottom) / 2;
              const nextScale = clampScale(DOUBLE_TAP_SCALE);
              const ratio = nextScale / scale;
              setScale(nextScale);
              // Clamp so a double-tap near the photo's edge can't push the
              // image off-screen — the anchor math alone is unbounded.
              setOffset(
                clampOffset(
                  {
                    x: offset.x + (e.clientX - centerX) * (1 - ratio),
                    y: offset.y + (e.clientY - centerY) * (1 - ratio),
                  },
                  nextScale,
                ),
              );
            } else {
              setScale(clampScale(DOUBLE_TAP_SCALE));
            }
          }
          return;
        }
        lastTapRef.current = { time: now, x: e.clientX, y: e.clientY };

        if (scale > 1) {
          dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            offsetX: offset.x,
            offsetY: offset.y,
          };
        }
      }
    },
    [scale, offset, reset, clampOffset],
  );

  const onPointerMove = useCallback(
    (e: React.PointerEvent<HTMLImageElement>) => {
      if (!pointersRef.current.has(e.pointerId)) return;
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (pointersRef.current.size === 2 && pinchRef.current) {
        const pts = Array.from(pointersRef.current.values());
        const dx = pts[0].x - pts[1].x;
        const dy = pts[0].y - pts[1].y;
        const dist = Math.hypot(dx, dy);
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;
        const distRatio = dist / pinchRef.current.startDistance;
        const next = clampScale(+(pinchRef.current.startScale * distRatio).toFixed(3));
        setScale(next);
        if (next <= MIN_SCALE) {
          setOffset({ x: 0, y: 0 });
        } else {
          // Anchor the zoom at the starting midpoint, then apply the pan
          // delta from the midpoint movement so the fingers' midpoint stays
          // pinned to the same image pixel as it grows or shrinks. The
          // result is then clamped so the image still covers the viewport.
          const scaleRatio = next / pinchRef.current.startScale;
          const zoomDx =
            (pinchRef.current.startMidX - pinchRef.current.centerX) * (1 - scaleRatio);
          const zoomDy =
            (pinchRef.current.startMidY - pinchRef.current.centerY) * (1 - scaleRatio);
          const panDx = midX - pinchRef.current.startMidX;
          const panDy = midY - pinchRef.current.startMidY;
          setOffset(
            clampOffset(
              {
                x: pinchRef.current.startOffsetX + zoomDx + panDx,
                y: pinchRef.current.startOffsetY + zoomDy + panDy,
              },
              next,
            ),
          );
        }
      } else if (dragRef.current) {
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        setOffset(
          clampOffset(
            { x: dragRef.current.offsetX + dx, y: dragRef.current.offsetY + dy },
            scaleRef.current,
          ),
        );
      }
    },
    [clampOffset],
  );

  const onPointerEnd = useCallback((e: React.PointerEvent<HTMLImageElement>) => {
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    if (pointersRef.current.size === 0) dragRef.current = null;
  }, []);

  // Reset transient toolbar state whenever the visible image changes (or the
  // lightbox is closed) so the next photo starts with a clean affordance.
  useEffect(() => {
    setCopied(false);
  }, [index, open]);

  const currentUrl = open ? images[index!] : null;
  const showNav = total > 1;
  const isZoomed = scale > 1;
  const zoomPercent = Math.round(scale * 100);

  const handleDownload = useCallback(async () => {
    if (!currentUrl || downloading) return;
    setDownloading(true);
    try {
      await downloadImage(currentUrl, downloadFileNameFor(currentUrl));
    } catch {
      toast({
        title: "Download failed",
        description: "The image could not be downloaded. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  }, [currentUrl, downloading, toast]);

  const handleCopy = useCallback(async () => {
    if (!currentUrl) return;
    const ok = await copyTextToClipboard(absoluteUrl(currentUrl));
    if (ok) {
      setCopied(true);
      toast({ title: "Image link copied" });
      window.setTimeout(() => setCopied(false), 1500);
    } else {
      toast({
        title: "Copy failed",
        description: "Your browser blocked clipboard access.",
        variant: "destructive",
      });
    }
  }, [currentUrl, toast]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-50 focus:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
          data-testid={`lightbox-${messageId}`}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">Image attachment viewer</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {showNav ? `Image ${(index ?? 0) + 1} of ${total}. Use the arrow keys to navigate between images.` : "Attached image"}
          </DialogPrimitive.Description>

          {/* Backdrop click target — fills the viewport behind the image. */}
          <button
            type="button"
            className="absolute inset-0 w-full h-full cursor-default focus:outline-none"
            onClick={onClose}
            aria-label="Close image viewer"
            data-testid={`lightbox-backdrop-${messageId}`}
          />

          {/* Centered image — clicks on the image itself do not close. */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center p-4 sm:p-8 overflow-hidden">
            {currentUrl && !loadError && (
              <img
                key={`${currentUrl}#${retryNonce}`}
                ref={imageRef}
                src={currentUrl}
                alt={showNav ? `Attached image ${(index ?? 0) + 1} of ${total}` : "Attached image"}
                className={cn(
                  "pointer-events-auto max-h-full max-w-full object-contain select-none touch-none",
                  isZoomed ? "cursor-grab active:cursor-grabbing" : "cursor-default",
                )}
                style={{
                  transform: `translate3d(${offset.x}px, ${offset.y}px, 0) scale(${scale})`,
                  transformOrigin: "center center",
                  willChange: "transform",
                }}
                data-testid={`lightbox-image-${messageId}`}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerEnd}
                onPointerCancel={onPointerEnd}
                onPointerLeave={onPointerEnd}
                onDragStart={(e) => e.preventDefault()}
                draggable={false}
                onError={() => setLoadError(true)}
              />
            )}
            {currentUrl && loadError && (
              <div
                className="pointer-events-auto flex max-w-md flex-col items-center gap-3 rounded-md bg-gray-900 px-6 py-6 text-center text-white shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                data-testid={`lightbox-error-${messageId}`}
              >
                <AlertTriangle className="h-8 w-8 text-amber-400" aria-hidden="true" />
                <div className="text-sm font-medium">Couldn't load this image</div>
                <div className="text-xs text-white/70">
                  Check your connection and try again, or download the file to view it locally.
                </div>
                <div className="mt-1 flex flex-wrap items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={handleRetry}
                    className="rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
                    data-testid={`lightbox-retry-${messageId}`}
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={handleDownload}
                    disabled={downloading}
                    className="inline-flex items-center gap-1.5 rounded-md bg-white/15 px-3 py-1.5 text-xs font-medium text-white hover:bg-white/25 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
                    data-testid={`lightbox-error-download-${messageId}`}
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </button>
                </div>
              </div>
            )}
          </div>

          <div
            className="absolute top-4 right-4 z-10 flex items-center gap-2"
            data-testid={`lightbox-toolbar-${messageId}`}
          >
            <button
              type="button"
              onClick={handleDownload}
              disabled={!currentUrl || downloading}
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Download image"
              title="Download image"
              data-testid={`lightbox-download-${messageId}`}
            >
              <Download className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={handleCopy}
              disabled={!currentUrl}
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
              aria-label={copied ? "Image link copied" : "Copy image link"}
              title={copied ? "Image link copied" : "Copy image link"}
              data-testid={`lightbox-copy-${messageId}`}
            >
              {copied ? <Check className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
            </button>
            <DialogPrimitive.Close
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Close image viewer"
              data-testid={`lightbox-close-${messageId}`}
            >
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>

          {showNav && (
            <div
              className="absolute top-4 left-4 z-10 rounded-full bg-black/60 px-3 py-1 text-xs font-medium text-white"
              data-testid={`lightbox-counter-${messageId}`}
            >
              {(index ?? 0) + 1} / {total}
            </div>
          )}

          {/* Zoom controls */}
          <div
            className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 flex items-center gap-1 rounded-full bg-black/60 px-2 py-1 text-white shadow-lg"
            data-testid={`lightbox-zoom-controls-${messageId}`}
          >
            <button
              type="button"
              onClick={zoomOut}
              disabled={scale <= MIN_SCALE}
              className="rounded-full p-1.5 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Zoom out"
              data-testid={`lightbox-zoom-out-${messageId}`}
            >
              <ZoomOut className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={reset}
              className="min-w-[3.5rem] rounded-full px-2 py-1 text-xs font-medium tabular-nums hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Reset zoom to fit"
              title="Reset zoom"
              data-testid={`lightbox-zoom-level-${messageId}`}
            >
              {zoomPercent}%
            </button>
            <button
              type="button"
              onClick={zoomIn}
              disabled={scale >= MAX_SCALE}
              className="rounded-full p-1.5 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Zoom in"
              data-testid={`lightbox-zoom-in-${messageId}`}
            >
              <ZoomIn className="h-4 w-4" />
            </button>
            <span className="mx-1 h-4 w-px bg-white/30" aria-hidden="true" />
            <button
              type="button"
              onClick={reset}
              disabled={scale === MIN_SCALE && offset.x === 0 && offset.y === 0}
              className="rounded-full p-1.5 hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Reset to fit"
              title="Reset to fit"
              data-testid={`lightbox-zoom-reset-${messageId}`}
            >
              <Maximize2 className="h-4 w-4" />
            </button>
          </div>

          {showNav && (
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-2 sm:left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Previous image"
              data-testid={`lightbox-prev-${messageId}`}
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}

          {showNav && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-2 sm:right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Next image"
              data-testid={`lightbox-next-${messageId}`}
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}

interface PdfLightboxProps {
  /** The PDF URL to display, or null when closed. */
  url: string | null;
  /** Stable prefix for test ids. */
  idPrefix: number | string;
  onClose: () => void;
}

/**
 * Fullscreen PDF viewer matching the {@link PhotoLightbox} treatment: dark
 * overlay, top-right Download + Close toolbar, embedded browser PDF renderer.
 * Used by surfaces (e.g. booking-request submissions) where a pay stub or
 * signed document should open in place instead of a separate tab.
 */
export function PdfLightbox({ url, idPrefix, onClose }: PdfLightboxProps) {
  const open = url !== null;
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);

  const handleDownload = useCallback(async () => {
    if (!url || downloading) return;
    setDownloading(true);
    try {
      await downloadImage(url, downloadFileNameFor(url));
    } catch {
      toast({
        title: "Download failed",
        description: "The document could not be downloaded. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  }, [url, downloading, toast]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogPortal>
        <DialogOverlay className="bg-black/90" />
        <DialogPrimitive.Content
          className={cn(
            "fixed inset-0 z-50 focus:outline-none",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          )}
          data-testid={`pdf-lightbox-${idPrefix}`}
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">Document viewer</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">Attached PDF document</DialogPrimitive.Description>

          <div className="absolute inset-0 flex items-center justify-center p-4 pt-16 sm:p-8 sm:pt-16">
            {url && (
              <iframe
                src={url}
                title="PDF document"
                className="h-full w-full max-w-4xl rounded-md bg-white shadow-2xl"
                data-testid={`pdf-lightbox-frame-${idPrefix}`}
              />
            )}
          </div>

          <div
            className="absolute top-4 right-4 z-10 flex items-center gap-2"
            data-testid={`pdf-lightbox-toolbar-${idPrefix}`}
          >
            <button
              type="button"
              onClick={handleDownload}
              disabled={!url || downloading}
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Download document"
              title="Download document"
              data-testid={`pdf-lightbox-download-${idPrefix}`}
            >
              <Download className="h-5 w-5" />
            </button>
            <DialogPrimitive.Close
              className="rounded-full bg-black/60 p-2 text-white hover:bg-black/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              aria-label="Close document viewer"
              data-testid={`pdf-lightbox-close-${idPrefix}`}
            >
              <X className="h-5 w-5" />
            </DialogPrimitive.Close>
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
